import { useState, useEffect } from 'react';
import translationService, { LANGUAGE_CHANGE_EVENT, TRANSLATIONS_LOADED_EVENT } from '../services/translationService';

// Define interface for the hook return type
export interface TranslationHookReturn {
  t: (key: string, params?: Record<string, string>, defaultValue?: string) => string;
  changeLanguage: (lang: string) => Promise<void>;
  currentLanguage: string;
  isChangingLanguage: boolean;
  isLoaded: boolean;
}

/**
 * Custom hook for using translations throughout the application
 * @returns {TranslationHookReturn} Translation utilities
 */
export function useTranslation(): TranslationHookReturn {
  // Get initial translations and language from service
  const initialTranslations = translationService.getTranslations() || {};
  const initialLanguage = translationService.getCurrentLanguage();
  const initialLoadedState = Object.keys(initialTranslations).length > 0;

  const [translations, setTranslations] = useState<Record<string, string>>(initialTranslations);
  const [currentLanguage, setCurrentLanguage] = useState<string>(initialLanguage);
  const [isChangingLanguage, setIsChangingLanguage] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(initialLoadedState);

  useEffect(() => {
    // Initial load check
    if (!isLoaded) {
      const serviceTranslations = translationService.getTranslations() || {};
      const hasServiceTranslations = Object.keys(serviceTranslations).length > 0;
      
      if (hasServiceTranslations) {
        setTranslations(serviceTranslations);
        setIsLoaded(true);
      }
    }
    
    // Listen for translations loaded events
    const handleTranslationsLoaded = (event: Event) => {
      const customEvent = event as CustomEvent;
      const details = customEvent.detail;
      
      setCurrentLanguage(details?.language || translationService.getCurrentLanguage());
      const newTranslations = translationService.getTranslations() || {};
      setTranslations(newTranslations);
      setIsLoaded(Object.keys(newTranslations).length > 0);
    };
    
    // Listen for language change events
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const details = customEvent.detail;
      
      setCurrentLanguage(details?.language || translationService.getCurrentLanguage());
      const newTranslations = translationService.getTranslations() || {};
      setTranslations(newTranslations);
      setIsLoaded(Object.keys(newTranslations).length > 0);
      setIsChangingLanguage(false);
    };
    
    // Force check translations after a short delay
    const timeoutId = setTimeout(() => {
      if (!isLoaded) {
        const serviceTranslations = translationService.getTranslations() || {};
        if (Object.keys(serviceTranslations).length > 0) {
          setTranslations(serviceTranslations);
          setIsLoaded(true);
        }
      }
    }, 1000);
    
    window.addEventListener(TRANSLATIONS_LOADED_EVENT, handleTranslationsLoaded);
    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    
    return () => {
      window.removeEventListener(TRANSLATIONS_LOADED_EVENT, handleTranslationsLoaded);
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
      clearTimeout(timeoutId);
    };
  }, [isLoaded]);

  /**
   * Translate a key to the current language with improved fallback handling
   * @param {string} key - The translation key
   * @param {Record<string, string>} params - Optional parameters for the translation
   * @param {string} defaultValue - Optional default value if translation is missing
   * @returns {string} Translated text
   */
  const t = (key: string, params?: Record<string, string>, defaultValue?: string): string => {
    // Check if we have a translation for this key
    const hasTranslation = translations && Object.prototype.hasOwnProperty.call(translations, key);
    
    // Get the translation or use fallbacks
    let translation = hasTranslation ? translations[key] : (defaultValue || key);
    
    // If the translation is empty, use default or key
    if (!translation || translation.trim() === '') {
      translation = defaultValue || key;
    }
    
    // Replace any parameters in the translation
    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        translation = translation.replace(`{${paramKey}}`, paramValue);
      });
    }
    
    return translation;
  };

  /**
   * Change the current language
   * @param {string} lang - The language code to change to
   */
  const changeLanguage = async (lang: string): Promise<void> => {
    if (lang && lang !== currentLanguage && !isChangingLanguage) {
      setIsChangingLanguage(true);
      await translationService.setLanguage(lang);
      // State will be updated via event listener
    }
  };

  return {
    t,
    changeLanguage,
    currentLanguage,
    isChangingLanguage,
    isLoaded
  };
} 